<template>
    <div class="zebContainer">
        <div class="titleContainer">
            <div class="common-title">{{ "제로 에너지 건축물 등급" }}</div>
            <span style="font-size: 14px; margin-left: 10px; cursor: pointer" @click="() => $refs.zebGradeModal.show()">
                <i class="fa fa-question-circle"></i>
            </span>
        </div>
        <div ref="chart" style="width: 100%; height: 86%"></div>
        <b-modal ref="zebGradeModal" hide-footer @hidden="() => $refs.zebGradeModal.hide()">
            <template #modal-header>
                <div class="d-flex justify-content-between align-items-center w-100">
                    <h5 class="mb-0">제로 에너지 건축물 등급 기준</h5>
                    <span class="setting-icon" @click="() => $refs.zebGradeModal.hide()">
                        <i class="fas fa-times"></i>
                    </span>
                </div>
            </template>
            <template #default>
                <table class="table table-bordered table-striped text-center">
                    <thead class="thead-dark">
                        <tr>
                            <th scope="col">ZEB 등급</th>
                            <th scope="col">등급 명칭</th>
                            <th scope="col">에너지 자립률 (%)</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(grade, index) in zebGradeCriteria" :key="`ZEB-GRADE-${index}`">
                            <td>{{ grade.code }}</td>
                            <td>{{ grade.name }}</td>
                            <td>{{ grade.rateLabel }}</td>
                        </tr>
                    </tbody>
                </table>
            </template>
        </b-modal>
    </div>
</template>

<script>
import * as echarts from "echarts";

const ZEB_GRADE_CRITERIA = [
    { grade: "ZEB Plus", code: "ZEB Plus", name: "+등급", min: 120, max: 140, rateLabel: "120 이상" },
    { grade: "ZEB1", code: "ZEB1", name: "1등급", min: 100, max: 120, rateLabel: "100 이상" },
    { grade: "ZEB2", code: "ZEB2", name: "2등급", min: 80, max: 100, rateLabel: "80 이상" },
    { grade: "ZEB3", code: "ZEB3", name: "3등급", min: 60, max: 80, rateLabel: "60 이상" },
    { grade: "ZEB4", code: "ZEB4", name: "4등급", min: 40, max: 60, rateLabel: "40 이상" },
    { grade: "ZEB5", code: "ZEB5", name: "5등급", min: 20, max: 40, rateLabel: "20 이상" },
];

const Y_LABELS = ZEB_GRADE_CRITERIA.map((r) => r.grade);
const STAIR_X_MAX = 120;
const BAR_AREA_MAX = 62;
const X_AXIS_MAX = 100;
/** pill 열 중심 x (x축 0~100). 간격·위치는 임의 — 막대(BAR_AREA_MAX) 오른쪽 여백만 확보하면 됨 */
const PILL_SLOT_X = { cert: 70, run: 82, totRun: 94 };
const PILL_SYMBOL_SIZE = [30, 20];

const CHART_PAD_TOP = 15;
const CHART_PAD_BOTTOM = 0;
/** 카테고리 밴드 대비 막대·간격 (픽셀 barWidth는 간격이 안 보일 수 있음) */
const BAR_CATEGORY_GAP = "20%";
const BAR_WIDTH = "90%";

const BAR_GRADIENTS = [
    ["#0D47A1", "#1565C0"],
    ["#1565C0", "#1E88E5"],
    ["#1976D2", "#42A5F5"],
    ["#1E88E5", "#64B5F6"],
    ["#42A5F5", "#90CAF9"],
    ["#64B5F6", "#BBDEFB"],
];

const THEME = {
    cert: { main: "#5C6BC0", light: "#7986CB" },
    run: { main: "#43A047", light: "#66BB6A" },
    totRun: { main: "#1E88E5", light: "#42A5F5" },
};

export default {
    props: {
        data: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            chart: null,
            zebGradeCriteria: ZEB_GRADE_CRITERIA.map((r) => ({
                code: r.code,
                name: r.name,
                rateLabel: r.rateLabel,
            })),
        };
    },
    computed: {
        timeName() {
            const headers = this.data?.headers;
            if (!Array.isArray(headers) || !headers[0]?.timeDsvn) {
                return "당월";
            }
            return headers[0].timeDsvn === "MONTH" ? "당월" : "올해";
        },
    },
    watch: {
        data: {
            handler() {
                this.$nextTick(() => this.renderChart());
            },
            deep: true,
            immediate: true,
        },
    },
    mounted() {
        window.addEventListener("resize", this.onResize);
        this.$nextTick(() => this.renderChart());
    },
    beforeDestroy() {
        window.removeEventListener("resize", this.onResize);
        this.disposeChart();
    },
    methods: {
        onResize() {
            if (!this.chart) return;
            this.chart.resize();
        },
        disposeChart() {
            if (this.chart) {
                this.chart.dispose();
                this.chart = null;
            }
        },
        resizeChart() {
            this.onResize();
        },
        getZebValues() {
            return this.data?.values?.zebGrade ?? null;
        },
        getCriteriaRow(grade) {
            return ZEB_GRADE_CRITERIA.find((r) => r.grade === grade);
        },
        barGradient(index) {
            const [from, to] = BAR_GRADIENTS[index] || BAR_GRADIENTS[BAR_GRADIENTS.length - 1];
            return new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                { offset: 0, color: from },
                { offset: 1, color: to },
            ]);
        },
        pillGradient(themeKey) {
            const t = THEME[themeKey];
            return new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: t.light },
                { offset: 1, color: t.main },
            ]);
        },
        buildStairValues() {
            return ZEB_GRADE_CRITERIA.map((row) => (row.min / STAIR_X_MAX) * BAR_AREA_MAX);
        },
        gradeToYIndex(grade) {
            if (!grade) return -1;
            return Y_LABELS.indexOf(grade);
        },
        buildBarSeriesData(stairValues, activeYIndexes) {
            return stairValues.map((value, dataIndex) => {
                const isActive = activeYIndexes.includes(dataIndex);
                return {
                    value,
                    itemStyle: {
                        color: this.barGradient(dataIndex),
                        borderRadius: [0, 8, 8, 0],
                        shadowColor: isActive ? "rgba(30, 136, 229, 0.45)" : "rgba(13, 71, 161, 0.12)",
                        shadowBlur: isActive ? 10 : 4,
                        shadowOffsetY: isActive ? 2 : 1,
                        borderColor: isActive ? "rgba(255,255,255,0.85)" : "rgba(255,255,255,0.35)",
                        borderWidth: isActive ? 1.5 : 1,
                    },
                };
            });
        },
        buildMarkPointData(zebValues) {
            const timeName = this.timeName;
            const indicators = [
                {
                    key: "cert",
                    grade: zebValues.certifiedZEBGrade,
                    shortLabel: "인증",
                    fullLabel: "인증등급",
                    selfRate: null,
                },
                {
                    key: "run",
                    grade: zebValues.measuredZEBGrade,
                    shortLabel: timeName,
                    fullLabel: `운영등급 (${timeName})`,
                    selfRate: zebValues.selfRate,
                },
                {
                    key: "totRun",
                    grade: zebValues.totalOperatingZEBGrade,
                    shortLabel: "전체",
                    fullLabel: "운영등급(전체)",
                    selfRate: zebValues.totalOperatingSelfRate,
                },
            ];

            return indicators
                .filter((ind) => this.gradeToYIndex(ind.grade) >= 0)
                .map((ind) => ({
                    name: ind.fullLabel,
                    coord: [PILL_SLOT_X[ind.key], this.gradeToYIndex(ind.grade)],
                    grade: ind.grade,
                    fullLabel: ind.fullLabel,
                    selfRate: ind.selfRate,
                    symbol: "roundRect",
                    symbolSize: PILL_SYMBOL_SIZE,
                    itemStyle: {
                        color: this.pillGradient(ind.key),
                        shadowBlur: 8,
                        shadowColor: "rgba(0, 0, 0, 0.18)",
                        shadowOffsetY: 2,
                        borderColor: "rgba(255, 255, 255, 0.45)",
                        borderWidth: 1,
                    },
                    label: {
                        show: true,
                        formatter: ind.shortLabel,
                        color: "#fff",
                        fontSize: 10,
                        fontWeight: 600,
                        fontFamily: "inherit",
                    },
                }));
        },
        buildOption(zebValues) {
            const stairValues = this.buildStairValues();
            const markPointData = this.buildMarkPointData(zebValues);
            const activeYIndexes = markPointData.map((mp) => this.gradeToYIndex(mp.grade)).filter((idx) => idx >= 0);

            const trackData = Y_LABELS.map(() => BAR_AREA_MAX);

            return {
                graphic: [],
                tooltip: {
                    trigger: "item",
                    confine: true,
                    backgroundColor: "rgba(255, 255, 255, 0.96)",
                    borderColor: "rgba(0, 0, 0, 0.06)",
                    borderWidth: 1,
                    padding: [10, 14],
                    textStyle: { color: "#37474F", fontSize: 12 },
                    extraCssText: "border-radius:10px;box-shadow:0 8px 24px rgba(0,0,0,0.1);",
                    formatter: (params) => {
                        if (params.componentType === "markPoint" && params.data?.fullLabel) {
                            const { fullLabel, grade, selfRate } = params.data;
                            const criteria = this.getCriteriaRow(grade);
                            const lines = [
                                `<div style="font-weight:600;color:#263238;margin-bottom:6px;">${fullLabel}</div>`,
                                `<div style="color:#546E7A;">등급 <b style="color:#1565C0;">${grade}</b></div>`,
                                criteria
                                    ? `<div style="color:#78909C;font-size:11px;margin-top:4px;">기준 ${criteria.rateLabel}</div>`
                                    : "",
                                selfRate != null
                                    ? `<div style="margin-top:6px;padding-top:6px;border-top:1px solid #ECEFF1;color:#37474F;">자립률 <b>${selfRate}%</b></div>`
                                    : "",
                            ].filter(Boolean);
                            return lines.join("");
                        }
                        if (params.seriesName === "ZEB 등급" && params.seriesType === "bar") {
                            const criteria = this.getCriteriaRow(Y_LABELS[params.dataIndex]);
                            if (criteria) {
                                return `<div style="font-weight:600;color:#263238;">${criteria.grade}</div>
                                    <div style="color:#78909C;font-size:11px;">${criteria.name} · ${criteria.rateLabel}</div>`;
                            }
                        }
                        return "";
                    },
                },
                legend: {
                    top: 6,
                    left: "center",
                    itemWidth: 8,
                    itemHeight: 8,
                    itemGap: 16,
                    icon: "circle",
                    textStyle: { fontSize: 10, color: "#607D8B" },
                    data: [
                        { name: "인증등급", itemStyle: { color: THEME.cert.main } },
                        { name: `운영등급 (${this.timeName})`, itemStyle: { color: THEME.run.main } },
                        { name: "운영등급(전체)", itemStyle: { color: THEME.totRun.main } },
                    ],
                },
                grid: {
                    top: CHART_PAD_TOP,
                    left: 4,
                    right: 6,
                    bottom: CHART_PAD_BOTTOM,
                    containLabel: true,
                },
                xAxis: {
                    type: "value",
                    min: 0,
                    max: X_AXIS_MAX,
                    show: false,
                },
                yAxis: {
                    type: "category",
                    inverse: true,
                    data: Y_LABELS,
                    axisLine: { show: false },
                    axisTick: { show: false },
                    axisLabel: {
                        margin: 10,
                        fontSize: 10,
                        fontWeight: 600,
                        color: "#37474F",
                    },
                },
                series: [
                    {
                        name: "ZEB 트랙",
                        type: "bar",
                        data: trackData,
                        barWidth: BAR_WIDTH,
                        barCategoryGap: BAR_CATEGORY_GAP,
                        barGap: "-100%",
                        silent: true,
                        z: 1,
                        itemStyle: {
                            color: "rgba(227, 242, 253, 0.65)",
                            borderRadius: [0, 8, 8, 0],
                            borderColor: "rgba(144, 202, 249, 0.35)",
                            borderWidth: 1,
                        },
                    },
                    {
                        name: "ZEB 등급",
                        type: "bar",
                        data: this.buildBarSeriesData(stairValues, activeYIndexes),
                        barWidth: BAR_WIDTH,
                        barCategoryGap: BAR_CATEGORY_GAP,
                        z: 10,
                        label: { show: false },
                        markPoint: {
                            symbolKeepAspect: false,
                            data: markPointData,
                        },
                    },
                ],
            };
        },
        renderChart() {
            const zebValues = this.getZebValues();
            const el = this.$refs.chart;

            if (!el || !zebValues) {
                if (!zebValues) this.disposeChart();
                return;
            }

            if (!this.chart) {
                this.chart = echarts.init(el);
            }

            this.chart.setOption(this.buildOption(zebValues), true);
            this.$nextTick(() => this.chart?.resize());
        },
    },
};
</script>

<style scoped>
.zebContainer {
    width: 100%;
    height: 100%;
}
.titleContainer {
    font-size: 18px;
    color: #4d4d4d;
    font-weight: 800;
    display: flex;
    width: 100%;
    justify-content: center;
    align-items: center;
}
</style>
